import { map as _map, isArray } from 'lodash';
import { format, isValid, parseISO } from 'date-fns';
import { INCOMPLETE_DOCS_ACTION_ID, CONSULTING_TIME_ACTION_ID } from '../../config';
import { getClaimStatusTranslation } from '../../helpers/langHelpers';

const calculateRejectedAmount = (invoice) => {
  if (!isArray(invoice.risks)) {
    return null;
  }

  const invoiceAmount = parseFloat(invoice.invoiceAmount);
  let totalAmount = 0;

  invoice.risks.forEach((risk) => {
    totalAmount += parseFloat(risk.amount);
  });

  return invoiceAmount >= totalAmount ? invoiceAmount - totalAmount : invoiceAmount;
};

const calculateDeductibleAmount = (invoice) => {
  if (!isArray(invoice.risks)) {
    return null;
  }

  let totalAmount = 0;
  let totalPaymentAmount = 0;
  let totalPaymentDeductible = 0;

  invoice.risks.forEach((risk) => {
    const { amount, paymentAmount, paymentDeductible } = risk;
    totalAmount += parseFloat(amount);
    totalPaymentAmount += parseFloat(paymentAmount);
    totalPaymentDeductible += parseFloat(paymentDeductible);
  });

  return (totalAmount - totalPaymentAmount) + totalPaymentDeductible;
};

const determineInvoiceStatus = (invoice, invoiceType) => {
  if (invoiceType === 'REFERRAL') {
    return 'REFERRED';
  }

  if (!isValid(parseISO(invoice.acceptDate))) {
    return 'RECEIVED';
  }

  if (!isValid(parseISO(invoice.invoiceSettleDate))) {
    return 'IN_REVIEW';
  }

  if (!isValid(parseISO(invoice.invoicePayDate))) {
    return 'PROCESSED';
  }

  return 'PAYMENT';
};

const determineInvoiceDetailedStatus = (
  invoice,
  invoiceType,
  invoiceStatus,
  incompleteDocs,
) => {
  if (invoiceType === 'REFERRAL') {
    return 'REFERRED';
  }

  switch (invoiceStatus) {
    case 'RECEIVED': {
      if (incompleteDocs) {
        return 'RECEIVED_INCOMPLETE';
      }
      return 'RECEIVED';
    }

    case 'IN_REVIEW': {
      return 'IN_REVIEW';
    }

    case 'PROCESSED': {
      const { invoiceAmount } = invoice;
      const rejectedAmount = calculateRejectedAmount(invoice);

      if (rejectedAmount === 0) {
        return 'FULL_COMPENSATION';
      }

      if (rejectedAmount < invoiceAmount) {
        return 'PARTIAL_COMPENSATION';
      }

      return 'DENIED';
    }

    default: {
      return 'PAYMENT';
    }
  }
};

const isInvoiceStateOpen = (invoiceStatus) => ['RECEIVED', 'IN_REVIEW'].includes(invoiceStatus);

const determineInvoiceType = (invoice) => (invoice.isGuarantee === '0' ? 'APPLICATION' : 'REFERRAL');

const formatDateIfValid = (dateString, formatString = 'dd/MM/yyyy') => {
  const isDateValid = isValid(parseISO(dateString));
  return isDateValid ? format(parseISO(dateString), formatString) : '';
};

export const findActionById = (actions, id) => {
  let action = actions.find(({ actionId }) => actionId === id);
  if (action) {
    action = {
      ...action,
      actionDate: formatDateIfValid(action.actionDate),
    };
  }
  return action;
};

const getPreparedInvoices = (item) => {
  const { claimNumber, invoices, actions } = item;

  return invoices.map((invoice, i) => {
    const {
      acceptDate,
      invoiceCurrency,
      invoiceInputDate,
      invoiceSettleDate,
      invoicePayDate,
      invoiceAmount,
      invoiceSettleAmount,
      invoiceName,
    } = invoice;

    const invoiceActions = actions.filter(
      (action) => action.actionDate === invoiceInputDate,
    );
    const incompleteDocsAction = findActionById(invoiceActions, INCOMPLETE_DOCS_ACTION_ID);
    const consultingTimeAction = findActionById(invoiceActions, CONSULTING_TIME_ACTION_ID);

    let rejectedAmount;
    let deductibleAmount = 0;
    if (invoice.risks && invoice.risks.length > 0) {
      rejectedAmount = calculateRejectedAmount(invoice);
      deductibleAmount = calculateDeductibleAmount(invoice);
    }

    const type = determineInvoiceType(invoice);
    const status = determineInvoiceStatus(invoice, type);
    const detailedStatus = determineInvoiceDetailedStatus(
      invoice,
      type,
      status,
      incompleteDocsAction,
    );
    const isStateOpen = isInvoiceStateOpen(status);

    const settledAmount = (status === 'PROCESSED' || status === 'PAYMENT')
      ? parseFloat(invoiceSettleAmount)
      : null;

    return {
      rowId: `${claimNumber}/${i}`,
      type,
      status,
      detailedStatus,
      isStateOpen,
      incompleteDocsAction,
      consultingTimeAction,
      invoiceCurrency,

      invoiceInputDate: formatDateIfValid(invoiceInputDate),
      acceptDate: formatDateIfValid(acceptDate),
      invoiceSettleDate: formatDateIfValid(invoiceSettleDate),
      invoicePayDate: formatDateIfValid(invoicePayDate),

      invoiceName,
      serviceName: getClaimStatusTranslation(invoice, 'serviceName'),
      serviceProvider: getClaimStatusTranslation(invoice, 'serviceProvider'),
      invoiceAmount: parseFloat(invoiceAmount),
      deductibleAmount,
      settledAmount,
      rejectedAmount,
    };
  });
};

const prepareClaims = (claims) => {
  if (!claims) {
    return [];
  }

  return _map(claims, (claim) => ({
    ...claim,
    invoices: getPreparedInvoices(claim),
  }));
};

export default prepareClaims;
